import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-community";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {Box, Grid, IconButton, Tooltip, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useProductCodeRepository} from "../../../api/repositories/ProductCodeRepository";
import {
    dateFormatterShort,
    fromGenericBeToGenericFe,
    i18nConvertNumberWithDecimals,
    onGridReady,
    resolveFilterCodes,
    useActionColDef
} from "../../../utils";
import DialogComponent from "../../../common/components/DialogComponent";
import {
    DialogInterface,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE
} from "../../../api/types/generic";
import {AgGridReact} from "ag-grid-react";
import BasicToolbar from "../../../common/components/BasicToolbar";
import {useNavigate} from "react-router-dom";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {useRecoilState} from "recoil";
import Loading from "../../../common/components/Loading";
import {useProductCodeLocationGroupRepository} from "../../../api/repositories/ProductCodeLocationGroupRepository";
import {useExceptionListRepository} from "../../../api/repositories/ExceptionListRepository";
import SearchFilters from "../../../common/components/SearchFilters";
import {FormSearchAssociationPlanModel} from "../../../api/types/associationPlan";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {DEFAULT_PLAN_COL_DEF, SET_SNACKBAR} from "../../../constants";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import ActionsPopover from "../../../common/components/ActionsPopover";
import {useEventRepository} from "../../../api/repositories/EventRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useRateDetailRepository} from "../../../api/repositories/RateDetailRepository";
import UpdateActionIcon from "../../../common/components/UpdateActionIcon";
import {DownloadRounded} from "@mui/icons-material";

type IProductData = {
    id: string;
    productCodeName: string;
}
type IProductLocationGroupData = IProductData & {
    locationGroupId: number | null;
    locationGroupName: string | null;
}
type IRatePlanData = IProductLocationGroupData & {
    rate: string | null;
    referenceRate: string | null;
    discount: string | null;
    exceptionList: string | null;
    moreThanOne: boolean;
    leadingPrice: number | null;
    reservationFrom: string | null;
    reservationTo: string | null;
    pickupFrom: string | null;
    pickupTo: string | null;
}

const RateProductCodeLocationGroupList = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use Navigate
    const navigate = useNavigate();

    // Use Repository Hooks
    const {getPagedProductCode} = useProductCodeRepository();
    const {getLocationGroupsByProductCodePaged} = useProductCodeLocationGroupRepository();
    const {getRatePlanPaged} = useExceptionListRepository();
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId
    } = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {getCompleteProduct} = useEventRepository();
    const {exportRateExcelProductCode} = useRateDetailRepository();
    const legalEntityRef = useRef("");

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchAssociationPlanModel>({
        commercialCategoryId: null,
        commercialSegmentId: null,
        productCode: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    const handleTriggerFilters = (entries: FormSearchAssociationPlanModel) => {
        const hardcodedFilter = Object.entries(entries).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [key]: {type: "contains", filter: value}
            }),
            {}
        );

        gridRef.current?.api.paginationGoToFirstPage();
        gridRef.current?.api.setFilterModel(hardcodedFilter);
        gridRef.current?.api.onFilterChanged();
    };

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        // LPP-24637: dealer e legalEntity sono obbligatori per il servizio di pricing.
        // Il reset deve ripristinare i valori del profilo utente (come lo stato iniziale),
        // non svuotarli: con le liste vuote la ricerca viene rifiutata con 400 "Validation failure".
        setTestFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            productCode: null,
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
        handleTriggerFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            productCode: null,
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            commercialCategoryId: (testFilters.commercialCategoryId as SelectElementModelFE)?.code as string,
            commercialSegmentId: (testFilters.commercialSegmentId as SelectElementModelFE)?.code as string,
            productCode: testFilters.productCode,
            legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
            dealers: resolveFilterCodes(testFilters.dealers, dealerFilteredOptions),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleGetStaticContent = async () => {
        const commercialCategoriesData: CommercialCategoryEntityResponse[] = await getCommercialCategories();

        if (!currentSegmentRef.current && !currentLegalEntitiesRef.current) {
            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryList(commercialCategoryList);
        }

        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] = fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // Ref per tracciare il segment corrente e ignorare risposte obsolete
    const currentSegmentRef = useRef<string | null>(null);
    const currentLegalEntitiesRef = useRef<string>("");

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    useEffect(() => {
        const segmentCode = testFilters.commercialSegmentId
            ? (testFilters.commercialSegmentId as SelectElementModelFE).code as string
            : null;
        
        const legalEntitiesKey = testFilters.legalEntities && Array.isArray(testFilters.legalEntities)
            ? resolveFilterCodes(testFilters.legalEntities, []).sort().join(",")
            : "";

        currentSegmentRef.current = segmentCode;
        currentLegalEntitiesRef.current = legalEntitiesKey;

        const getContent = async () => {
            let commercialCategoriesData: CommercialCategoryEntityResponse[];
            
            if (segmentCode) {
                commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId(segmentCode);
            } else {
                commercialCategoriesData = await getCommercialCategories();
            }

            if (currentSegmentRef.current !== segmentCode || currentLegalEntitiesRef.current !== legalEntitiesKey) {
                return;
            }

            const allCategories: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            
            const selectedLegalEntities = testFilters.legalEntities;
            const selectedLegalEntityCodes = resolveFilterCodes(selectedLegalEntities, []);
            // null = nessun filtro per legal entity (mostra tutte le categorie)
            let legalEntityCodes: string[] | null = selectedLegalEntityCodes.length > 0
                ? selectedLegalEntityCodes
                : null;
            
            // Per single-tenant, usa automaticamente il tenant dell'utente
            if (!legalEntityCodes && user.legalEntities?.length === 1) {
                legalEntityCodes = [user.legalEntities[0].name];
            }
            
            const expandedCategories = new Map<string, CommercialCategorySelectFE>();
            allCategories.forEach(category => {
                if (category.labels && category.labels.length > 0) {
                    category.labels.forEach(labelObj => {
                        if (!legalEntityCodes || legalEntityCodes.includes(labelObj.country)) {
                            const key = labelObj.label;
                            if (!expandedCategories.has(key)) {
                                expandedCategories.set(key, {
                                    ...category,
                                    label: labelObj.label
                                });
                            }
                        }
                    });
                } else {
                    expandedCategories.set(category.label, category);
                }
            });
            setCommercialCategoryList(Array.from(expandedCategories.values()));
        };
        getContent();
    }, [testFilters.commercialSegmentId, testFilters.legalEntities]);

    const downloadButtonClicked = async (data: any) => {
        const rateId = data.rate.split("-")[0].trim();
        await exportRateExcelProductCode(rateId, data.id, legalEntityRef.current)
    }

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "productCode",
            value: testFilters.productCode,
            label: `${t("pricing:associationPlan.form.labels.productCode")}`,
            handleChange: (value: string) => {
                setTestFilters({
                    ...testFilters,
                    productCode: value
                });
            },
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegmentId",
            value: testFilters.commercialSegmentId,
            label: `${t("pricing:associationPlan.form.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null
                });
            },
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId,
            label: `${t("pricing:associationPlan.form.labels.commercialCategory")}`,
            handleChange: (value: CommercialCategorySelectFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                });
            },
            options: commercialCategoryList,
            type: "Autocomplete"
        }
    ];

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOption || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    // ************ AG GRID START ************ //
    const {actionColDef} = useActionColDef();

    const columnDefs: ColDef[] = [
        {
            field: "id",
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.productCode"),
            rowGroup: true,
            hide: true,
            pinned: "left",
            cellRenderer: (params: ICellRendererParams) => params.data.id + " - " + params.data.productCodeName
        },
        {
            field: "legalEntities",
            headerName: t("pricing:peaks.table.headers.legalEntities"),
            headerTooltip: t("pricing:upsellingRate.table.headers.legalEntities"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            pinned: "left",
            hide: true
        },
        {
            field: "dealers",
            headerName: t("pricing:peaks.table.headers.dealers"),
            headerTooltip: t("pricing:upsellingRate.table.headers.dealers"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.dealer)),
            pinned: "left",
            hide: true
        },
        {
            field: "locationGroupId",
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.locationGroup"),
            rowGroup: true,
            sortable: false,
            hide: true,
            pinned: "left",
            cellRenderer: (params: ICellRendererParams) => params.data.locationGroupName
        },
        {
            field: "rate",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.rate"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.rate")
        },
        {
            field: "referenceRate",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.referenceRate"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.referenceRate")
        },
        {
            field: "discount",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.discount"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.discount"),
            cellRenderer: (params: ICellRendererParams) => params.data.discount
        },
        // {
        //     field: "exceptionList",
        //     flex: 1,
        //     headerName: t("pricing:rateProductCodeLocationGroup.table.headers.exceptionList"),
        //     headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.exceptionList"),
        //     cellRenderer: (params: ICellRendererParams) => `${params.data.exceptionList || ""}${params.data.moreThanOne ? "*" : ""}`
        // },
        {
            field: "leadingPrice",
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.leadingPrice"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.leadingPrice"),
            flex: 1
        },
        {
            field: "reservationFrom",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.resFrom"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.reservationFrom")
        },
        {
            field: "reservationTo",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.resTo"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.reservationTo")
        },
        {
            field: "pickupFrom",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.pickupFrom"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.pickupFrom")
        },
        {
            field: "pickupTo",
            flex: 1,
            headerName: t("pricing:rateProductCodeLocationGroup.table.headers.pickupTo"),
            headerTooltip: t("pricing:rateProductCodeLocationGroup.table.headers.pickupTo")
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => {
                return !params.data.rate ?
                    params.data.locationGroupId ? (
                        !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_LOCATION_RATE_PLAN_LIST";
                            });
                        }) ? (
                            <UpdateActionIcon
                                params={params}
                                action={() =>
                                    navigate(
                                        "/pricing/product-code/location-group-rate/create",
                                        {
                                            replace: true,
                                            state: {
                                                productCodeId: params.data.id,
                                                locationGroupId: params.data.locationGroupId,
                                            },
                                        }
                                    )
                                }
                                styles={{
                                    margin: "auto",
                                    color: theme.palette.accent.main,
                                }}
                            />
                        ) : undefined
                    ) : (
                        <ActionsPopover
                            params={params}
                            complete={{
                                action: async () => {
                                    try {
                                        await getCompleteProduct(params.data.id);
                                        //Success message
                                        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
                                    } catch (error) {

                                    }
                                }, userHasAuth: !!user.profiles.find((profile: any) => {
                                    return profile.auths.find((auth: any) => {
                                        return auth.name === "SCOPE_PRC_COMPLETE_RATE_PLAN_LIST"
                                    })
                                })
                            }}
                            update={{
                                action: () =>
                                    navigate(
                                        "/pricing/product-code/location-group-rate/create",
                                        {
                                            replace: true,
                                            state: {
                                                productCodeId: params.data.id,
                                                locationGroupId: params.data.locationGroupId,
                                            },
                                        }
                                    ), userHasAuth: !!user.profiles.find((profile: any) => {
                                    return profile.auths.find((auth: any) => {
                                        return auth.name === "SCOPE_PRC_UPDATE_RATE_PLAN_LIST"
                                    })
                                })
                            }}
                        />
                    ) : (
                        <Tooltip arrow title="download" placement={"right-start"}>
                            <IconButton
                                aria-label="download"
                                onClick={() => downloadButtonClicked(params.data)}
                                color="accent"
                                sx={{
                                    margin: "auto",
                                    color: theme.palette.accent.main
                                }}
                            >
                                <DownloadRounded/>
                            </IconButton>
                        </Tooltip>
                    )
            }
        },


        {
            field: "productCode",
            colId: "productCode",
            hide: true,
            filter: "text"
        },
        {
            field: "commercialCategoryId",
            colId: "commercialCategoryId",
            hide: true,
            filter: "text"
        },
        {
            field: "commercialSegmentId",
            colId: "commercialSegmentId",
            hide: true,
            filter: "text"
        }
    ];
    const autoGroupColumnDef = useMemo<ColDef>(() => {
        return {
            minWidth: 200,
            sortable: true
        };
    }, []);

    const defaultColDef = DEFAULT_PLAN_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchAssociationPlanModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce((acc, [key, value]) => ({
                    ...acc,
                    [key]: value?.filter || ""
                }), testFilters);

                let rowCount = 0;
                let rowData: IProductData[] | IProductLocationGroupData[] | IRatePlanData[] = [];

                // @ts-ignore
                let size = params.request.endRow - params.request.startRow;
                // @ts-ignore
                let page = params.request.startRow > 0 ? Math.floor(params.request.startRow / size) : 0;

                const groupKeys = params.request.groupKeys;
                if (!groupKeys || groupKeys.length === 0) {
                    //caricamento codici prodotto
                    const productCodePaged = await getPagedProductCode({
                        ...filter,
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    if (productCodePaged.totalElements === 0) {
                        gridRef.current!.api.showNoRowsOverlay();
                    } else {
                        gridRef.current!.api.hideOverlay();
                    }

                    rowCount = productCodePaged.totalElements;
                    rowData = productCodePaged.content.map(pc => ({
                        id: pc.id as string,
                        productCodeName: pc.name
                    })) as IProductData[];
                } else if (groupKeys.length === 1 && groupKeys[0]) {
                    //caricamento gruppi di location
                    const locationGroupList = await getLocationGroupsByProductCodePaged({
                        productCode: groupKeys[0],
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    rowCount = locationGroupList.totalElements;
                    rowData = locationGroupList.content.map(lg => ({
                        id: groupKeys[0] as string,
                        productCodeName: groupKeys[0] as string,
                        locationGroupId: lg.id,
                        locationGroupName: lg.name
                    })) as IProductLocationGroupData[];
                }
                //caricamento ratePlan
                else if (groupKeys.length === 2 && groupKeys[0] && groupKeys[1]) {
                    const ratePlans = await getRatePlanPaged({
                        productCode: groupKeys[0],
                        locationGroupId: Number(groupKeys[1]),
                        dealers: testFilters.dealers,
                        legalEntities: testFilters.legalEntities,
                        showOnlyActive: true,
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    rowCount = ratePlans.totalElements;
                    rowData = ratePlans.content.map(ratePlan => ({
                        id: groupKeys[0] as string,
                        productCodeName: "",
                        locationGroupId: Number(groupKeys[1]),
                        locationGroupName: "",
                        rate: ratePlan.rate?.id + " - " + ratePlan.rate?.name,
                        referenceRate: (ratePlan.rate?.referenceTariff as SelectElementModelBE)?.name || "",
                        discount: ratePlan.rate?.relatedDiscountIncreaseFixed ?
                            i18nConvertNumberWithDecimals(Number(ratePlan.rate.relatedDiscountIncreaseFixed) / 100)
                            :
                            ratePlan.rate?.relatedDiscountIncreasePercentage ?
                                ratePlan.rate?.relatedDiscountIncreasePercentage + "%"
                                :
                                "",
                        exceptionList: ratePlan.exceptionList?.name,
                        moreThanOne: ratePlan.moreThanOne,
                        leadingPrice: ratePlan.rateDetail?.amount ? i18nConvertNumberWithDecimals(Number(ratePlan.rateDetail?.amount as number) / 100) : null,
                        reservationFrom: dateFormatterShort(ratePlan.rate?.reservationFrom),
                        reservationTo: dateFormatterShort(ratePlan.rate?.reservationTo),
                        pickupFrom: dateFormatterShort(ratePlan.rate?.pickupDateFrom),
                        pickupTo: dateFormatterShort(ratePlan.rate?.pickupDateTo)
                    })) as IRatePlanData[];
                    legalEntityRef.current = ratePlans.content.map(ratePlans => ratePlans.rate?.legalEntity as string)[0];
                }

                params.success({
                    rowData: rowData,
                    rowCount: rowCount
                });
            }
        };
    };
    // ************ AG GRID END ************ //

    const toolbar = {
        renderActions: () => (
            <BasicToolbar addIcon={
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_CREATE_RATE_PLAN_LIST";
                    });
                }) ?
                    {
                        onClick: () => navigate("/pricing/product-code/location-group-rate/create")
                    } : undefined}/>
        )
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_RATE_PLAN_LIST.name")}/>


            <SearchFilters
                filtersOutOfCollapse={filtersOutOfCollapse}
                filtersInCollapse={filtersInCollapse}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        autoGroupColumnDef={autoGroupColumnDef}
                        animateRows={true}
                        toolbar={toolbar}
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        serverSideFilteringAlwaysResets={true}
                        onGridReady={() => onGridReady({
                            initialSortColId: "name",
                            gridRef,
                            createDatasource
                        })}
                        groupDisplayType={"multipleColumns"}
                    />
                    {dialog?.open &&
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    }
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default RateProductCodeLocationGroupList;